// Copyright (c) 2020 by Audere
//
// Use of this source code is governed by an LGPL-3.0 license that
// can be found in the LICENSE file distributed with this file.

import { FIELDS_SUFFIX, SYMPTOMS } from "./SymptomsConstants";
import { FORMID, TestRunParams } from "../TestRun/TestRunConstants";
import { ROUTE_DEFINITIONS, getPath } from "routes/routes";
import React, { ReactElement, memo, useCallback, useEffect } from "react";
import { Theme, createStyles, makeStyles, useTheme } from "@material-ui/core";
import { updateSymptomsList, useTestRunState } from "store/testrun";
import { useHistory, useParams } from "react-router-dom";

import Grid from "@material-ui/core/Grid";
import QuestionnaireForm from "../ui/Questionnaire/Questionnaire";
import QuestionnaireInput from "components/ui/Questionnaire/QuestionnaireInput";
import { confirmAlert } from "../../utils/confirmAlert";
import { cx } from "style/utils";
import { setStepReady } from "components/TestRun/TestRunStepReadyEmitter";
import { useAppDispatch } from "store";
import { useAuthState } from "../../store/auth";
import { useTestRunConfig } from "components/TestRun/useTestRunConfig";

function getFields(reportedSymptoms: {
  [key: string]: any;
}): {
  startQuestions: ReactElement;
  _48hQuestions: ReactElement;
  severityQuestions: ReactElement;
} {
  const fields: {
    startQuestions: Array<ReactElement>;
    _48hQuestions: Array<ReactElement>;
    severityQuestions: Array<ReactElement>;
  } = {
    startQuestions: [],
    _48hQuestions: [],
    severityQuestions: [],
  };

  SYMPTOMS.forEach(symptom => {
    if (reportedSymptoms[symptom.name]) {
      let fieldName = symptom.name + FIELDS_SUFFIX.START;
      fields.startQuestions.push(
        <Grid item xs={12} sm={6} md={4} key={fieldName}>
          <QuestionnaireInput
            label={symptom.label}
            mandatory={true}
            name={fieldName}
            value={reportedSymptoms[symptom.name].start}
            placeholder={""}
            type="date"
          />
        </Grid>
      );

      fieldName = symptom.name + FIELDS_SUFFIX._48H;
      fields._48hQuestions.push(
        <Grid item xs={12} sm={6} md={4} key={fieldName}>
          <QuestionnaireInput
            label={symptom.label}
            mandatory={true}
            name={fieldName}
            placeholder={"Select one"}
            selectOptions={[
              { value: "true", name: "Yes" },
              { value: "false", name: "No" },
            ]}
            value={
              reportedSymptoms[symptom.name]._48h !== undefined
                ? reportedSymptoms[symptom.name]._48h + ""
                : undefined
            }
            type={"buttongroup"}
          />
        </Grid>
      );

      fieldName = symptom.name + FIELDS_SUFFIX._SEVERITY;
      fields.severityQuestions.push(
        <Grid item xs={12} sm={6} md={4} key={fieldName}>
          <QuestionnaireInput
            label={symptom.label}
            mandatory={true}
            name={fieldName}
            placeholder={"Select one"}
            selectOptions={[
              { value: "mild", name: "Mild" },
              { value: "moderate", name: "Moderate" },
              { value: "severe", name: "Severe" },
            ]}
            value={reportedSymptoms[symptom.name].severity}
            type={"buttongroup"}
          />
        </Grid>
      );
    }
  });

  return {
    startQuestions: (
      <Grid container spacing={2}>
        {fields.startQuestions}
      </Grid>
    ),
    _48hQuestions: (
      <Grid container spacing={2}>
        {fields._48hQuestions}
      </Grid>
    ),
    severityQuestions: (
      <Grid container spacing={2}>
        {fields.severityQuestions}
      </Grid>
    ),
  };
}

const useStyle = makeStyles((theme: Theme) =>
  createStyles({
    question: {
      fontWeight: "bold",
      marginBottom: "0 !important",
    },
    questionSubtext: {
      marginBottom: "1em",
    },
  })
);

export default () => {
  const history = useHistory();
  const { auth } = useAuthState();
  const { step, testRunUID } = useParams<TestRunParams>();
  const QuestionnaireFormMemoized = memo(QuestionnaireForm);
  const classes = useStyle();
  const theme = useTheme();
  const { getNextDefaultStep } = useTestRunConfig();
  const dispatch = useAppDispatch();
  const testRunState = useTestRunState();

  const stepReadyCallback = useCallback(
    (stepReady: boolean) => {
      setStepReady(step, stepReady);
    },
    [step]
  );

  const reportedSymptoms = testRunState?.testRun?.reportedSymptoms || {};

  // If there's no symptoms recorded, we can enable the "next" button.
  useEffect(() => {
    if (Object.getOwnPropertyNames(reportedSymptoms).length === 0) {
      stepReadyCallback(true);
    }
  }, [testRunState, stepReadyCallback, reportedSymptoms]);

  if (!auth || !testRunState.subscribed) {
    return <div />; // Add Loader
  }

  if (
    testRunState?.testRun?.reportedSymptoms &&
    Object.getOwnPropertyNames(reportedSymptoms).length === 0
  ) {
    // No symptoms recorded.
    return (
      <div>
        No reported symptoms, you can go to the next step.
        <form
          id={FORMID}
          onSubmit={() => {
            const nextStep = getNextDefaultStep({ currentStepName: step! });
            // This is a dummy form, only here to go to the next page.
            const path = getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
              params: {
                ":testRunUID": testRunUID,
                ":step": nextStep!,
              },
            });
            history.push(path);
          }}
        />
      </div>
    );
  }

  const { startQuestions, _48hQuestions, severityQuestions } = getFields(
    reportedSymptoms
  );

  return (
    <QuestionnaireFormMemoized
      formId={FORMID}
      onFormReady={stepReadyCallback}
      onSubmit={async (event, questions) => {
        try {
          event.preventDefault();
          const updatedSymptoms: any = {};

          Object.getOwnPropertyNames(reportedSymptoms).forEach(symptomName => {
            updatedSymptoms[symptomName] = {
              start: questions[symptomName + FIELDS_SUFFIX.START].value,
              _48h:
                questions[symptomName + FIELDS_SUFFIX._48H].value === "true",
              severity: questions[symptomName + FIELDS_SUFFIX._SEVERITY].value,
            };
          });

          await dispatch(
            updateSymptomsList({ reportedSymptoms: updatedSymptoms })
          );

          // Resets the state.
          stepReadyCallback(false);
          const nextStep = getNextDefaultStep({ currentStepName: step! });
          const path = getPath(ROUTE_DEFINITIONS.PERFORMTEST, {
            params: {
              ":testRunUID": testRunUID,
              ":step": nextStep!,
            },
          });
          history.push(path);
        } catch (e) {
          confirmAlert({ title: "oops", message: e.message, theme });
        }
      }}
    >
      <div className={cx({ [classes.question]: true })}>
        How long ago did the symptoms start?
      </div>
      <div className={classes.questionSubtext}>
        <i>Select the timeframe that best applies</i>
      </div>
      {startQuestions}
      <div className={cx({ [classes.question]: true })}>
        Were these symptoms present in the last 48 hours?
      </div>
      <div className={classes.questionSubtext}></div>
      {_48hQuestions}
      <div className={cx({ [classes.question]: true })}>
        How severe were your symptoms?
      </div>
      <div className={classes.questionSubtext}>
        <i>Select the level of discomfort you felt at the worst point</i>
      </div>
      {severityQuestions}
    </QuestionnaireFormMemoized>
  );
};
